import React, {useEffect, useId, useMemo, useRef, useState} from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    ButtonBase,
    CircularProgress,
    DialogActions,
    DialogContent,
    LinearProgress,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {ArrowBack, ArrowForward, CampaignOutlined, Check} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import {useDispatch, useSelector} from 'react-redux';
import {useForm} from 'react-hook-form';
import {campaignFormActions as actions} from '../../store/actions/campaignFormAction';
import {campaignActions} from '../../store/actions/campaignsAction';
import {flowActions} from '../../store/actions/flowAction';
import {alertActions} from '../../store/actions/alertActions';
import {campaignServices} from '../../service/campaignService';
import {trafficSourceServices} from '../../service/trafficSourceService';
import {trackingDomainServices} from '../../service/trackingDomainService';
import {cloakingDomainServices} from '../../service/cloakingDomainService';
import {flowServices} from '../../service/flowService';
import {integrationService} from '../../service/integrationService';
import {countries, currencies} from '../utils/DataList';
import {FlowDialog} from '../flows/FlowDesign';
import {CampaignDialogHeader} from './CampaignDesign';
import CampaignSetup, {supportsCampaignConversions} from './CampaignSetup';
import CampaignDestination from './CampaignDestination';
import {buildCampaignPayload, getCampaignFlowIssues, getCampaignRequestError} from './campaignFormUtils';
import {getSettingsColors} from '../settings/SettingsDesign';

function initialValues() {
    let settings;
    try {
        settings = JSON.parse(localStorage.getItem('user-settings'));
    } catch {
        settings = null;
    }
    return {
        name: '', workspace: null, trafficSource: null, trackingDomain: null,
        country: countries.find(item => item.code === ''), tags: [],
        currency: currencies.find(item => item.code === settings?.currencyCode) || currencies.find(item => item.code === 'EUR'),
        costModel: 'auto', costValue: '', conversionAction: null, conversionPixel: null,
        conversionActionId: 0, conversionPixelId: 0, destinationType: 'flow', destinationUrl: '',
        sharedFlow: null, redirectMode: '302', cloakingDomain: null
    };
}

export default function CampaignEditor({mode = 'create', history, location, match}) {
    const dispatch = useDispatch();
    const {flow} = useSelector(state => state.campaignForm);
    const form = useForm({defaultValues: initialValues(), mode: 'onChange'});
    const {reset, watch, getValues, setValue, trigger} = form;
    const [step, setStep] = useState(0);
    const [visitedDestination, setVisitedDestination] = useState(false);
    const [loading, setLoading] = useState(mode !== 'create');
    const [loadFailed, setLoadFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [setupValidationAttempted, setSetupValidationAttempted] = useState(false);
    const [flowValidationAttempted, setFlowValidationAttempted] = useState(false);
    const [focusIssue, setFocusIssue] = useState(null);
    const [workspaceId, setWorkspaceId] = useState(0);
    const [integration, setIntegration] = useState(null);
    const [integrationLoading, setIntegrationLoading] = useState(false);
    const [integrationError, setIntegrationError] = useState('');
    const [integrationRetry, setIntegrationRetry] = useState(0);
    const [open, setOpen] = useState(true);
    const submitting = useRef(false);
    const contentRef = useRef(null);
    const formId = useId();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const id = match?.params?.id;
    const integrationId = watch('trafficSource')?.integrationId || 0;
    const destinationType = watch('destinationType');
    const flowIssues = useMemo(() => flowValidationAttempted && destinationType === 'flow' ? getCampaignFlowIssues(flow) : [], [flow, flowValidationAttempted, destinationType]);

    useEffect(() => {
        if (!focusIssue) {
            return;
        }
        const input = contentRef.current?.querySelector(`[data-flow-field="${focusIssue.field}"]`);
        input?.focus({preventScroll: true});
        input?.scrollIntoView?.({block: 'center', behavior: 'smooth'});
    }, [focusIssue]);

    const showFlowIssue = issue => {
        if (issue.pathIndex === undefined) {
            dispatch(actions.updateSelectedRule(issue.ruleIndex, flow.rules[issue.ruleIndex]));
        } else {
            dispatch(actions.updateSelectedPath(issue.ruleIndex, issue.pathIndex, flow.rules[issue.ruleIndex].paths[issue.pathIndex]));
        }
        setFocusIssue({...issue});
    };

    useEffect(() => {
        let active = true;
        dispatch(actions.resetForm());
        dispatch(flowActions.updateSharedFlow(null));
        if (mode === 'create') {
            return () => {
                active = false;
            };
        }

        async function load() {
            try {
                const campaign = await campaignServices.find(id);
                if (!(campaign?.id > 0)) {
                    throw new Error();
                }

                const [traffic, trackingDomain, cloakingDomain, sharedFlow] = await Promise.all([
                    trafficSourceServices.find(campaign.trafficId),
                    trackingDomainServices.find(campaign.trackingDomainId),
                    campaign.cloakingDomainId > 0 ? cloakingDomainServices.find(campaign.cloakingDomainId) : null,
                    campaign.flowId > 0 ? flowServices.find(campaign.flowId) : null
                ]);

                if (!active) {
                    return;
                }

                const trafficSource = {value: traffic.id, label: traffic.name, integrationId: traffic.integrationId};

                for (let i = 1; i <= 10; i++) trafficSource[`custom${i}`] = traffic[`custom${i}Name`];

                reset({
                    ...initialValues(), ...campaign,
                    name: campaign.name + (mode === 'duplicate' ? ' - [copy]' : ''),
                    trafficSource, trackingDomain: {value: trackingDomain.id, label: trackingDomain.domain},
                    cloakingDomain: cloakingDomain ? {value: cloakingDomain.id, label: cloakingDomain.domain} : null,
                    country: countries.find(item => item.code === campaign.countryCode) || initialValues().country,
                    currency: currencies.find(item => item.code === campaign.currencyCode) || initialValues().currency,
                    sharedFlow: sharedFlow ? {value: sharedFlow.id, label: `${sharedFlow.countryCode || 'Global'} - ${sharedFlow.name}`, flow: sharedFlow.flow} : null,
                    tags: campaign.tags || []
                });

                setWorkspaceId(campaign.workspaceId);
                dispatch(actions.updateTrafficSource(trafficSource));

                if (campaign.destinationType === 'flow') {
                    const routing = typeof campaign.flow === 'string' ? JSON.parse(campaign.flow) : campaign.flow;
                    if (!routing?.rules?.[0]?.paths?.length) throw new Error();
                    dispatch(actions.setFlow(routing));
                    dispatch(actions.updateSelectedPath(0, 0, routing.rules[0].paths[0]));
                }
            } catch (failure) {
                if (active) {
                    setLoadFailed(true);
                    setError(getCampaignRequestError(failure, 'Unable to load this campaign. Close and reopen to try again.'));
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        }

        load();
        return () => {
            active = false;
        };
    }, [mode, id, reset, dispatch]);

    useEffect(() => {
        let active = true;
        setIntegrationError('');
        setIntegration(null);
        if (!integrationId) {
            setIntegrationLoading(false);
            return () => {
                active = false;
            };
        }
        setIntegrationLoading(true);
        integrationService.findIntegration(integrationId).then(result => {
            if (!active) {
                return;
            }
            setIntegration(result);
            if (supportsCampaignConversions(result)) {
                if (!getValues('conversionAction')) {
                    setValue('conversionAction', result.integrationEvents?.find(item => item.id === getValues('conversionActionId')) || null);
                }
                if (!getValues('conversionPixel')) {
                    setValue('conversionPixel', result.integrationPixels?.find(item => item.id === getValues('conversionPixelId')) || null);
                }
            } else {
                setValue('conversionAction', null);
                setValue('conversionPixel', null);
            }
        }).catch(() => {
            if (active) {
                setIntegrationError('Unable to load the traffic source integration. Please retry before continuing.');
            }
        }).finally(() => {
            if (active) {
                setIntegrationLoading(false);
            }
        });

        return () => {
            active = false;
        };
    }, [integrationId, integrationRetry, getValues, setValue]);

    const close = (event, reason) => {
        if (reason === 'backdropClick' || submitting.current) {
            return;
        }
        dispatch(actions.resetForm());
        dispatch(flowActions.updateSharedFlow(null));
        dispatch(alertActions.clear());
        setOpen(false);
        history.push({pathname: '/campaigns', state: {from: location}});
    };

    const changeStep = value => {
        setStep(value);
        contentRef.current?.scrollTo?.({top: 0});
    };

    const continueToDestination = async () => {
        const names = ['name', 'workspace', 'trafficSource', 'trackingDomain', 'costModel', 'currency'];
        if (['cpc', 'cpa', 'cps'].includes(getValues('costModel'))) {
            names.push('costValue');
        }

        setSetupValidationAttempted(true);
        if (!(await trigger(names, {shouldFocus: true}))) {
            contentRef.current?.scrollTo?.({top: 0});
            return;
        }

        const data = getValues();

        dispatch(actions.formStep1(data));
        dispatch(actions.formStep2(data.workspace, data.name, data.country, data.trackingDomain, data.tags));
        setVisitedDestination(true);

        changeStep(1);
    };

    const submit = async event => {
        event.preventDefault();
        if (event.target.id !== formId || submitting.current || loading || loadFailed || integrationLoading || integrationError) return;
        if (step === 0) {
            await continueToDestination();
            return;
        }
        submitting.current = true;
        try {
            const data = getValues();
            const fields = data.destinationType === 'url' ? ['destinationUrl'] : data.destinationType === 'sharedFlow' ? ['sharedFlow'] : [];
            if (data.redirectMode === 'doubleMeta') {
                fields.push('cloakingDomain');
            }
            if (fields.length && !(await trigger(fields, {shouldFocus: true}))) {
                return;
            }
            const issues = data.destinationType === 'flow' ? getCampaignFlowIssues(flow) : [];
            setFlowValidationAttempted(true);
            if (issues.length) {
                showFlowIssue(issues[0]);
                return;
            }

            setSaving(true);
            setError('');

            const payload = buildCampaignPayload(data, flow);
            const result = mode === 'edit' ? await campaignServices.update(id, payload) : await campaignServices.create(payload);
            const campaignId = mode === 'edit' ? id : result?.id;

            if (!(Number(campaignId) > 0)) {
                throw new Error();
            }

            dispatch(campaignActions.rerender());
            dispatch(actions.resetForm());
            dispatch(flowActions.updateSharedFlow(null));
            dispatch(alertActions.clear());
            setOpen(false);
            history.push({pathname: `/campaigns/links/${parseInt(campaignId, 10)}`, state: {background: {pathname: '/campaigns'}}});
        } catch (failure) {
            setError(getCampaignRequestError(failure, 'Unable to save this campaign. Please try again.'));
            contentRef.current?.scrollTo?.({top: 0});
        } finally {
            submitting.current = false;
            setSaving(false);
        }
    };

    const title = mode === 'create' ? 'New campaign' : mode === 'edit' ? 'Edit campaign' : 'Duplicate campaign';
    const setupHasErrors = ['name', 'workspace', 'trafficSource', 'trackingDomain', 'costModel', 'currency', ...(['cpc', 'cpa', 'cps'].includes(watch('costModel')) ? ['costValue'] : [])].some(name => form.formState.errors[name]);

    return <FlowDialog
        open={open}
        onClose={close}
        fullWidth
        fullScreen={fullScreen}
        maxWidth="lg"
        aria-labelledby="campaign-editor-title"
        sx={{
            '& .MuiDialog-paper': {
                height: {sm: 'min(850px, calc(100% - 64px))'},
                '& input, & textarea, & .MuiSelect-select': {fontSize: {xs: '16px', sm: '13px'}}
            },
            [theme.breakpoints.down('sm')]: {
                '& .flow-path-row, & .flow-destination-row': {display: 'grid', rowGap: 1.5, py: 1.5},
                '& .flow-path-row': {
                    gridTemplateColumns: '24px minmax(0, 1fr) 40px 28px',
                    '& > .MuiRadio-root': {gridRow: 1, gridColumn: 1},
                    '& > .MuiTextField-root:first-of-type': {gridRow: 1, gridColumn: '2 / -1'},
                    '& > .MuiTextField-root:nth-of-type(2)': {gridRow: 2, gridColumn: 2, width: 116},
                    '& > .MuiSwitch-root': {gridRow: 2, gridColumn: 3}, '& > .MuiIconButton-root': {gridRow: 2, gridColumn: 4}
                },
                '& .flow-destination-row': {
                    gridTemplateColumns: '16px minmax(0, 1fr) repeat(3, 28px)',
                    '& > .MuiTypography-root': {gridRow: 1, gridColumn: 1},
                    '& > .MuiBox-root': {gridRow: 1, gridColumn: '2 / -1'},
                    '& > .MuiTextField-root': {gridRow: 2, gridColumn: 2, width: 116},
                    '& > button:nth-of-type(1)': {gridRow: 2, gridColumn: 3},
                    '& > button:nth-of-type(2)': {gridRow: 2, gridColumn: 4},
                    '& > button:nth-of-type(3)': {gridRow: 2, gridColumn: 5}
                }
            },
            '& .MuiDialog-paper .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {border: '1px solid', borderColor: 'error.main'},
            '& .MuiDialogContent-root': {pt: 2}, '& .MuiDialogActions-root': {flexWrap: 'wrap'}
        }}
    >
        <CampaignDialogHeader
            id="campaign-editor-title"
            title={title}
            icon={CampaignOutlined}
            onClose={saving ? undefined : close}
            description={mode === 'duplicate' ? 'Start with a copy, adjust its settings, and create a separate campaign.' : 'Configure your campaign, then decide where to send your traffic.'}
        />
        <Box
            component="nav"
            aria-label="Campaign setup steps"
            sx={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, mx: {xs: 2, sm: 3}, mb: 1}}>
            {['Campaign setup', 'Destination'].map((label, index) =>
                <ButtonBase
                    key={label}
                    disabled={loading || saving || loadFailed || (index === 1 && (!visitedDestination || integrationLoading || !!integrationError))}
                    onClick={() => {
                        if (index === 1) continueToDestination(); else changeStep(0);
                    }}
                    aria-current={step === index ? 'step' : undefined}
                    sx={{
                        display: 'flex', gap: {xs: 0.75, sm: 1.25}, justifyContent: 'flex-start', p: {xs: 1, sm: 1.5}, borderRadius: '10px', textAlign: 'left',
                        bgcolor: theme => step === index ? alpha(theme.palette.primary.main, 0.09) : getSettingsColors(theme).inset, color: step === index ? 'primary.main' : 'text.secondary'
                    }}
                >
                    <Box sx={{
                        display: 'grid', placeItems: 'center', flexShrink: 0, width: {xs: 24, sm: 28}, height: {xs: 24, sm: 28}, borderRadius: '50%', fontSize: 12, fontWeight: 600,
                        bgcolor: step === index ? 'primary.main' : 'background.darkBlue', color: step === index ? 'primary.contrastText' : 'text.secondary'
                    }}>
                        {index === 0 && visitedDestination && step === 1 ? <Check sx={{fontSize: 17}}/> : index + 1}
                    </Box>
                    <Box>
                        <Typography sx={{fontSize: {xs: 12, sm: 13}, fontWeight: 600}}>
                            {fullScreen && index === 0 ? 'Setup' : label}
                        </Typography>
                        <Typography sx={{display: {xs: 'none', sm: 'block'}, fontSize: 12, color: 'text.secondary'}}>
                            {index === 0 ? 'Details, traffic source & costs' : 'Paths, rules or a direct URL'}
                        </Typography>
                    </Box>
                </ButtonBase>)}
        </Box>

        {(loading || integrationLoading) &&
            <LinearProgress
                aria-label={loading ? 'Loading campaign' : 'Loading integration'}
                sx={{mx: {xs: 2, sm: 3}, borderRadius: 1}}
            />
        }

        {error && <Alert severity="error" variant="filled" sx={{
            mx: {xs: 2, sm: 3}, my: 1, flexShrink: 0,
            '& .MuiAlert-message': {minWidth: 0, maxHeight: 140, overflow: 'auto', overflowWrap: 'anywhere'}, '& .MuiAlertTitle-root': {fontWeight: 700}
        }}>
            <AlertTitle>{loadFailed ? 'Campaign could not be loaded' : 'Campaign could not be saved'}</AlertTitle>
            {error}
        </Alert>}
        {integrationError && <Alert severity="error" variant="filled" sx={{mx: {xs: 2, sm: 3}, my: 1, flexShrink: 0}}
                                    action={<Button color="inherit"
                                                    onClick={() => setIntegrationRetry(value => value + 1)}>Retry</Button>}>
            <AlertTitle>Traffic source integration unavailable</AlertTitle>{integrationError}
        </Alert>}

        <DialogContent ref={contentRef}>
            {step === 0 && setupValidationAttempted && setupHasErrors && <Alert severity="error" sx={{mb: 2}}>
                <AlertTitle>Complete the required campaign details</AlertTitle>Review the fields marked in red before
                continuing.
            </Alert>}
            {step === 1 && flowIssues.length > 0 &&
                <Alert severity="error" sx={{mb: 2, '& .MuiAlert-message': {minWidth: 0, width: '100%'}}}>
                    <AlertTitle>Fix {flowIssues.length} {flowIssues.length === 1 ? 'flow issue' : 'flow issues'} before
                        saving</AlertTitle>
                    <Typography sx={{fontSize: 12, mb: 0.5}}>Select an issue to open the affected rule or
                        path.</Typography>
                    <Box component="ul" sx={{m: 0, pl: 2, maxHeight: 160, overflow: 'auto'}}>
                        {flowIssues.map(issue => <Box component="li" key={issue.field}><Button color="inherit"
                                                                                               onClick={() => showFlowIssue(issue)}
                                                                                               sx={{textAlign: 'left', justifyContent: 'flex-start', px: 0, py: 0.25, minHeight: '28px !important', fontSize: 12, textDecoration: 'underline'}}>{issue.message}</Button></Box>)}
                    </Box>
                </Alert>}
            {!loading && !loadFailed && <Box component="form" id={formId} onSubmit={submit} autoComplete="off">
                <Box component="fieldset" disabled={saving} sx={{m: 0, p: 0, border: 0, minWidth: 0}}>
                    <Box hidden={step !== 0}>
                        <CampaignSetup
                            form={form}
                            workspaceId={workspaceId}
                            integration={integration}
                            onTrafficSourceChange={value => {
                                dispatch(actions.updateTrafficSource(value));
                                setValue('conversionAction', null);
                                setValue('conversionPixel', null);
                                setValue('conversionActionId', 0);
                                setValue('conversionPixelId', 0);
                            }}
                        />
                    </Box>

                    {visitedDestination &&
                        <Box hidden={step !== 1}>
                            <CampaignDestination form={form} validationErrors={flowIssues}/>
                        </Box>
                    }

                </Box>
            </Box>}
        </DialogContent>
        <DialogActions disableSpacing sx={{'& .MuiButton-root': {minWidth: 0, px: {xs: 1.5, sm: 2}, fontSize: 13}}}>
            <Typography
                sx={{fontSize: 12, color: 'text.secondary', mr: 'auto', display: {xs: 'none', sm: 'block'}}}>{step === 0 ? 'Step 1 of 2' : 'Campaign links are ready after saving.'}</Typography>
            <Button color="inherit" onClick={close} disabled={saving}>Cancel</Button>
            {step === 1 && <Button color="inherit" startIcon={<ArrowBack/>}
                                   sx={{'& .MuiButton-startIcon': {display: {xs: 'none', sm: 'inherit'}}}}
                                   onClick={() => changeStep(0)} disabled={saving}>Back</Button>}
            <Button variant="contained" type="submit" form={formId}
                    disabled={loading || saving || loadFailed || integrationLoading || !!integrationError}
                    endIcon={step === 0 ? <ArrowForward/> : undefined}
                    startIcon={saving ? <CircularProgress size={16} color="inherit"/> : undefined}>
                {saving ? 'Saving…' : step === 0 ? 'Continue' : mode === 'edit' ? 'Save changes' : mode === 'duplicate' ? 'Create copy' : 'Create campaign'}
            </Button>
        </DialogActions>
    </FlowDialog>;
}
